// GLSL sources for GradientBg3D — production subset only.
// The full experimental look catalog lives in gradientShaderCatalog.ts and
// is dynamically imported on demand (the /backgrounds dev routes), keeping
// ~55KB of shader strings out of the main bundle.

export type GradientLook = "mesh" | "orb" | "waves" | "rays" | "flag" | "banner" | "pennant"
  | "beam" | "shafts" | "veil"
  | "silk" | "halo" | "dune" | "nebula" | "ribbon" | "grid" | "particles" | "eclipse"
  | "polar" | "caustics" | "pulse" | "strands" | "horizon"
  | "mist" | "smoke" | "lantern" | "topo" | "circuit" | "orbits" | "flare" | "embers" | "surge"
  | "swirl" | "tide" | "drops" | "breath" | "duotone" | "frame"
  | "layers" | "tunnel" | "window" | "clouds" | "rain" | "snow" | "bars" | "scan" | "scope"
  | "shards" | "lens" | "crystal" | "blot" | "marble" | "wash" | "spot" | "duo" | "curtain"
  | "sunrise" | "moon" | "comet" | "weave" | "halftone" | "grain" | "arches" | "columns" | "steps"
  | "sigil" | "draw" | "ignite" | "plot" | "sign";

export const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

export const FRAG_HEAD = `
precision mediump float;
uniform vec2 uRes;
uniform float uTime;
uniform float uAngle;
uniform float uSeed;
uniform vec3 uColA;
uniform vec3 uColB;
uniform vec3 uColC;
uniform vec3 uColD;

vec2 dirFromAngle() { return vec2(cos(uAngle), sin(uAngle)); }

vec2 rotUv(vec2 uv) {
  float c = cos(uAngle);
  float s = sin(uAngle);
  vec2 p = uv - 0.5;
  return vec2(c * p.x - s * p.y, s * p.x + c * p.y) + 0.5;
}

float hash(float n) { return fract(sin(n) * 43758.5453); }

float noise1(float x) {
  float i = floor(x);
  float f = fract(x);
  return mix(hash(i), hash(i + 1.0), f * f * (3.0 - 2.0 * f));
}

float hash2(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }

float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash2(i), hash2(i + vec2(1.0, 0.0)), f.x),
             mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), f.x), f.y);
}

float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 3; i++) { v += a * noise2(p); p *= 2.03; a *= 0.5; }
  return v;
}

vec3 finish(vec3 col, vec2 uv) {
  // gentle vignette keeps edges calm
  col *= 0.86 + 0.14 * smoothstep(0.95, 0.25, distance(uv, vec2(0.5, 0.45)));
  // dither against banding
  float n = fract(sin(dot(gl_FragCoord.xy, vec2(12.9898, 78.233))) * 43758.5453);
  return col + (n - 0.5) * (2.0 / 255.0);
}
`;

// iconomy y-mark as an exact signed distance field — the two logo polygons
// traced vertex-for-vertex from Logo-Square.svg (1024 box), normalized to
// [-0.5, 0.5] with y up. Orange arm + tail = 7-gon, navy wedge = 4-gon
// (its collinear intermediate points collapsed). Even-odd polygon SDF,
// negative inside, flat angled cuts preserved.
// The mark slides in from off-screen left and settles on the right third
// (ease-out quint over ~2.6s); reduced-motion still frames render settled.
// The three center-line segments are kept as PATHS for the self-drawing
// looks that need a parametrization along the strokes (draw/ignite/plot/sign).
export const Y_HEAD = `
const vec2 Y_O0 = vec2( 0.1016, -0.0545);
const vec2 Y_O1 = vec2( 0.0065,  0.1343);
const vec2 Y_O2 = vec2(-0.1847,  0.4891);
const vec2 Y_O3 = vec2(-0.3975,  0.4891);
const vec2 Y_O4 = vec2(-0.0970, -0.0545);
const vec2 Y_O5 = vec2(-0.3407, -0.4891);
const vec2 Y_O6 = vec2(-0.1351, -0.4891);

const vec2 Y_N0 = vec2( 0.3976,  0.4891);
const vec2 Y_N1 = vec2( 0.1919,  0.4891);
const vec2 Y_N2 = vec2( 0.0571,  0.2312);
const vec2 Y_N3 = vec2( 0.1548,  0.0435);

const vec2 Y_A1 = vec2(-0.291,  0.489);
const vec2 Y_A2 = vec2( 0.002, -0.055);
const vec2 Y_B1 = vec2( 0.002, -0.055);
const vec2 Y_B2 = vec2(-0.238, -0.489);
const vec2 Y_C1 = vec2( 0.295,  0.489);
const vec2 Y_C2 = vec2( 0.106,  0.138);
const float Y_WA = 0.093;
const float Y_WB = 0.090;
const float Y_WC = 0.090;

float segd(vec2 p, vec2 a, vec2 b, out float h) {
  vec2 pa = p - a;
  vec2 ba = b - a;
  h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}

// one polygon-SDF edge step (iq even-odd construction)
void pEdge(vec2 p, vec2 vi, vec2 vj, inout float d, inout float s) {
  vec2 e = vj - vi;
  vec2 w = p - vi;
  vec2 b = w - e * clamp(dot(w, e) / dot(e, e), 0.0, 1.0);
  d = min(d, dot(b, b));
  bool c1 = p.y >= vi.y;
  bool c2 = p.y < vj.y;
  bool c3 = e.x * w.y > e.y * w.x;
  if ((c1 && c2 && c3) || (!c1 && !c2 && !c3)) s = -s;
}

float sdYOrange(vec2 p) {
  float d = dot(p - Y_O0, p - Y_O0);
  float s = 1.0;
  pEdge(p, Y_O0, Y_O6, d, s);
  pEdge(p, Y_O1, Y_O0, d, s);
  pEdge(p, Y_O2, Y_O1, d, s);
  pEdge(p, Y_O3, Y_O2, d, s);
  pEdge(p, Y_O4, Y_O3, d, s);
  pEdge(p, Y_O5, Y_O4, d, s);
  pEdge(p, Y_O6, Y_O5, d, s);
  return s * sqrt(d);
}

float sdYNavy(vec2 p) {
  float d = dot(p - Y_N0, p - Y_N0);
  float s = 1.0;
  pEdge(p, Y_N0, Y_N3, d, s);
  pEdge(p, Y_N1, Y_N0, d, s);
  pEdge(p, Y_N2, Y_N1, d, s);
  pEdge(p, Y_N3, Y_N2, d, s);
  return s * sqrt(d);
}

// exact signed distance to the full mark, negative inside
float yDist(vec2 p) {
  return min(sdYOrange(p), sdYNavy(p));
}

// slide-in progress: 0 at mount, 1 once settled (ease-out quint, ~2.6s)
float yProg(float t) {
  float x = clamp(t / 2.6, 0.0, 1.0);
  float inv = 1.0 - x;
  return 1.0 - inv * inv * inv * inv * inv;
}

// local mark space: enters from off-screen left, settles on the right third,
// ~4/5 of viewport height. Left half of the frame stays clear once settled.
vec2 yLocal(vec2 uv, float t) {
  float aspect = uRes.x / uRes.y;
  float cx0 = -0.45 / min(aspect, 1.0);   // fully off-screen at any aspect
  float cx = mix(cx0, 0.71, yProg(t));
  return vec2((uv.x - cx) * aspect, uv.y - 0.5) / 0.8;
}

// motion streak while sliding — ghost outlines at earlier positions,
// weighted by remaining travel so the trail vanishes once settled
float yTrail(vec2 uv, float t) {
  float moving = 1.0 - yProg(t);
  if (moving < 0.001) return 0.0;
  float g = 0.0;
  g += exp(-abs(yDist(yLocal(uv, t - 0.12))) * 26.0) * 0.30;
  g += exp(-abs(yDist(yLocal(uv, t - 0.24))) * 26.0) * 0.15;
  g += exp(-abs(yDist(yLocal(uv, t - 0.36))) * 26.0) * 0.10;
  return g * moving;
}

// ---- self-drawing looks: the mark stays put and writes itself in place ----
// settled mark space (no slide-in), same size/position as a settled yLocal
vec2 yLocalFixed(vec2 uv) {
  float aspect = uRes.x / uRes.y;
  return vec2((uv.x - 0.71) * aspect, uv.y - 0.5) / 0.8;
}

// linear draw progress 0..1 over dur seconds, then holds
float yDrawProg(float t, float dur) {
  return clamp(t / dur, 0.0, 1.0);
}

// writing coordinate: where a point sits along the pen's path, 0..1, following
// arm A -> tail B -> navy arm C. The exact logo shape (yDist) is what gets
// rendered; this only decides the order it is revealed in. A point is placed
// by its nearest centre-stroke, so the reveal wipes the true outline in place.
// Also outputs the pen tip at the current progress and a writing flag.
float yPathU(vec2 p, float prog, out vec2 tip, out float writing) {
  float hA; float dA = segd(p, Y_A1, Y_A2, hA);
  float hB; float dB = segd(p, Y_B1, Y_B2, hB);
  float hC; float dC = segd(p, Y_C1, Y_C2, hC);

  float u    = hA / 3.0;                          // assume arm A
  float best = dA;
  if (dB < best) { best = dB; u = (1.0 + hB) / 3.0; }   // tail B
  if (dC < best) { best = dC; u = (2.0 + hC) / 3.0; }   // navy arm C

  float seg = clamp(prog, 0.0, 1.0) * 3.0;
  if (seg < 1.0)      tip = mix(Y_A1, Y_A2, seg);
  else if (seg < 2.0) tip = mix(Y_B1, Y_B2, seg - 1.0);
  else                tip = mix(Y_C1, Y_C2, seg - 2.0);

  writing = step(prog, 0.999) * step(0.001, prog);
  return u;
}
`;

export const PROD_FRAGS: Partial<Record<GradientLook, string>> = {

  mesh: FRAG_HEAD + `
void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  // faster, more alive time base (was 0.1)
  float t = uTime * 0.2;

  // seed shifts anchor homes and phases -> distinct layout per seed
  float s1 = hash(uSeed * 7.13) - 0.5;
  float s2 = hash(uSeed * 13.7) - 0.5;
  float s3 = hash(uSeed * 23.3) - 0.5;
  float ph = uSeed * 11.0;

  // four color anchors on wider, quicker independent orbits
  vec2 p0 = vec2(0.16 + s1 * 0.3 + 0.17 * sin(t * 0.8 + ph),        0.30 + s2 * 0.3 + 0.19 * cos(t * 0.7 + ph));
  vec2 p1 = vec2(0.85 + s2 * 0.25 + 0.14 * cos(t * 0.6 + 2.0 + ph), 0.24 + s3 * 0.3 + 0.17 * sin(t * 0.9 + 1.0 + ph));
  vec2 p2 = vec2(0.72 + s3 * 0.3 + 0.19 * sin(t * 0.7 + 4.0 + ph),  0.82 + s1 * 0.25 + 0.14 * cos(t * 0.8 + 3.0 + ph));
  vec2 p3 = vec2(0.24 + s1 * 0.25 + 0.17 * cos(t * 0.9 + 5.0 + ph), 0.74 + s3 * 0.3 + 0.19 * sin(t * 0.6 + 2.5 + ph));

  // inverse-distance blend -> smooth mesh
  float w0 = 1.0 / pow(distance(uv, p0) + 0.09, 2.3);
  float w1 = 1.0 / pow(distance(uv, p1) + 0.09, 2.3);
  float w2 = 1.0 / pow(distance(uv, p2) + 0.09, 2.3);
  float w3 = 1.0 / pow(distance(uv, p3) + 0.09, 2.3);
  vec3 col = (uColA * w0 + uColB * w1 + uColC * w2 + uColD * w3) / (w0 + w1 + w2 + w3);

  // travelling diagonal light sweep, a touch brighter
  float band = sin((uv.x - uv.y) * 3.2 + t * 1.2);
  col += vec3(0.45, 0.68, 0.85) * smoothstep(0.80, 1.0, band) * 0.14;

  gl_FragColor = vec4(finish(col, uv), 1.0);
}
`,

  circuit: FRAG_HEAD + `
void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float t = uTime * 0.1;

  vec3 col = mix(uColA * 0.88, uColB * 0.65, uv.y * 0.55 + uv.x * 0.2);

  // faint tech lines with light pulses travelling along them
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float y = 0.08 + 0.84 * hash(fi * 17.3);
    float d = abs(uv.y - y);
    col += uColC * exp(-d * 260.0) * 0.08;
    float px = fract(t * (0.05 + hash(fi * 3.1) * 0.07) + hash(fi * 7.7));
    float pulse = exp(-pow((uv.x - px) * 26.0, 2.0)) * exp(-d * 200.0);
    col += uColC * pulse * 0.5;
  }

  gl_FragColor = vec4(finish(col, uv), 1.0);
}
`,

  waves: FRAG_HEAD + `
void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float t = uTime * 0.1;

  vec3 col = mix(uColA, uColB * 0.85, uv.y * 0.85);

  // five silky light lines drifting across the surface
  for (int i = 0; i < 5; i++) {
    float fi = float(i);
    float y = 0.18 + fi * 0.155
      + 0.06 * sin(uv.x * (2.2 + fi * 0.5) + t * (0.5 + fi * 0.11) + fi * 1.7)
      + 0.03 * sin(uv.x * (5.0 + fi) - t * 0.35 + fi * 4.0);
    float d = abs(uv.y - y);
    vec3 lineCol = mix(uColC, uColD + vec3(0.2), fi / 4.0);
    col += lineCol * exp(-d * 110.0) * 0.55;   // bright core
    col += lineCol * exp(-d * 16.0) * 0.16;    // soft halo
  }

  gl_FragColor = vec4(finish(col, uv), 1.0);
}
`,

  orbits: FRAG_HEAD + `
void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float aspect = uRes.x / uRes.y;
  vec2 p = vec2((uv.x - 0.5) * aspect + 0.5, uv.y);
  float t = uTime * 0.1;

  vec3 col = mix(uColA * 0.85, uColB * 0.55, uv.y * 0.7);

  vec2 c = vec2(0.55 + 0.5 * (aspect - 1.0) * 0.3, 0.52);
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    float r = 0.16 + fi * 0.13;
    vec2 q = (p - c) / vec2(1.0, 0.55);
    float d = abs(length(q) - r);
    col += uColC * exp(-d * d * 5000.0) * 0.1;               // path
    float th = t * (0.32 - fi * 0.08) + fi * 2.2;
    vec2 dp = c + vec2(cos(th), sin(th) * 0.55) * r;
    float dd = distance(p, dp);
    col += uColC * exp(-dd * dd * 1600.0) * 0.55;            // satellite
    col += uColD * exp(-dd * dd * 300.0) * 0.15;             // its glow
  }

  gl_FragColor = vec4(finish(col, uv), 1.0);
}
`,

  silk: FRAG_HEAD + `
void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float t = uTime * 0.1;

  // domain warp: folds slowly slide over each other
  float n = fbm(uv * 3.0 + vec2(t * 0.15, -t * 0.1));
  float m = fbm(uv * 2.0 + vec2(-t * 0.1, t * 0.12) + n * 1.4);

  vec3 col = mix(uColA * 0.9, uColB * 0.8, uv.y * 0.5 + n * 0.4);

  float fold = 0.5 + 0.5 * sin(uv.x * 2.6 + uv.y * 4.2 + m * 4.5 + t * 0.3);
  col += uColC * pow(fold, 3.0) * 0.28;          // sheen on the fold crests
  col += uColD * pow(1.0 - fold, 4.0) * 0.10;    // faint counter-light in the valleys

  gl_FragColor = vec4(finish(col, uv), 1.0);
}
`,

  polar: FRAG_HEAD + `
void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float t = uTime * 0.1;

  vec3 col = mix(uColA * 0.75, uColB * 0.45, uv.y * 0.6);

  // two aurora curtains, warped and drifting
  float c1 = pow(fbm(vec2(uv.x * 3.0 + t * 0.2, uv.y * 0.7 - t * 0.12)), 2.2);
  float c2 = pow(fbm(vec2(uv.x * 4.5 - t * 0.15 + 5.0, uv.y * 0.9 + t * 0.08)), 2.6);
  float height = smoothstep(0.05, 0.75, uv.y);

  col += mix(uColC, uColD + vec3(0.1), uv.y) * c1 * height * 0.55;
  col += uColC * c2 * height * 0.3;

  gl_FragColor = vec4(finish(col, uv), 1.0);
}
`,
};
